import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  Users, UserPlus, Upload, Search, Trash2, Link2, Send,
  ChevronDown, X, CheckCircle, AlertCircle, Shield, Briefcase, User,
  Download, FileSpreadsheet, Bell, CheckSquare, Square
} from "lucide-react";

import { API } from "../lib/api";

const ROLE_ICONS = {
  executive: Shield,
  manager: Briefcase,
  individual_contributor: User,
};

const ROLE_COLORS = {
  executive: { bg: "bg-purple-50", text: "text-purple-700", dot: "bg-purple-500" },
  manager: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  individual_contributor: { bg: "bg-slate-50", text: "text-slate-600", dot: "bg-slate-400" },
};

const ROLE_LABELS = {
  executive: "Executive",
  manager: "Manager",
  individual_contributor: "IC",
};

function InviteStatusBadge({ status }) {
  const map = {
    not_sent: { bg: "bg-gray-100", text: "text-gray-500", label: "Not Invited" },
    pending: { bg: "bg-amber-50", text: "text-amber-600", label: "Pending" },
    accepted: { bg: "bg-emerald-50", text: "text-emerald-600", label: "Accepted" },
  };
  const s = map[status] || map.not_sent;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-ibm-plex font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

export default function EmployeesPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", email: "", role: "", team: "", project: "", reporting_manager_email: "" });
  const [addError, setAddError] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [inviteLoading, setInviteLoading] = useState({});
  const [copiedToken, setCopiedToken] = useState(null);
  const [stats, setStats] = useState(null);

  // ─── Bulk selection state ─────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // "invite" | "notify"
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ subject: "", message: "" });
  const [notifyError, setNotifyError] = useState("");

  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(`${API}/api/org/employees`, { withCredentials: true });
      setEmployees(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/org/stats`, { withCredentials: true });
      setStats(data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchStats();
  }, []);

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || !addForm.email.trim()) {
      setAddError("Name and email are required");
      return;
    }
    setAddLoading(true);
    setAddError("");
    try {
      await axios.post(`${API}/api/org/employees`, addForm, { withCredentials: true });
      setShowAddModal(false);
      setAddForm({ name: "", email: "", role: "", team: "", project: "", reporting_manager_email: "" });
      fetchEmployees();
      fetchStats();
    } catch (err) {
      setAddError(err.response?.data?.detail || "Failed to add employee");
    } finally {
      setAddLoading(false);
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvLoading(true);
    setCsvResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/org/employees/csv`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setCsvResult(data);
      fetchEmployees();
      fetchStats();
    } catch (err) {
      setCsvResult({ error: err.response?.data?.detail || "Upload failed" });
    } finally {
      setCsvLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this employee?")) return;
    try {
      await axios.delete(`${API}/api/org/employees/${id}`, { withCredentials: true });
      setEmployees((prev) => prev.filter((e) => e.id !== id));
      fetchStats();
    } catch (err) {
      console.error(err);
    }
  };

  const handleInvite = async (id) => {
    setInviteLoading((prev) => ({ ...prev, [id]: true }));
    try {
      const { data } = await axios.post(`${API}/api/org/invite/${id}`, {}, { withCredentials: true });
      navigator.clipboard.writeText(data.invite_link);
      setCopiedToken(id);
      setTimeout(() => setCopiedToken(null), 3000);
      fetchEmployees();
    } catch (err) {
      console.error(err);
    } finally {
      setInviteLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const handleInviteAll = async () => {
    try {
      const { data } = await axios.post(`${API}/api/org/invite-all`, {}, { withCredentials: true });
      const count = data.count || 0;
      const sent = data.emails_sent || 0;
      let msg;
      if (count === 0) {
        msg = "No pending employees to invite. All employees have already accepted their invitations.";
      } else if (sent === count) {
        msg = `Generated ${count} invite links and sent ${sent} emails.`;
      } else if (sent > 0) {
        msg = `Generated ${count} invite links. ${sent} email${sent === 1 ? "" : "s"} delivered; the rest have copyable links in the table.`;
      } else if (data.email_configured === false) {
        msg = `Generated ${count} invite links. Email delivery isn't configured — copy the individual links from the table.`;
      } else {
        msg = `Generated ${count} invite links. Email delivery is in test mode — verify a domain in Resend to deliver to all employees. Copy the individual links from the table for now.`;
      }
      alert(msg);
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Failed to generate invite links");
    }
  };

  const handleDownloadTemplate = (format = "csv") => {
    const url = `${API}/api/org/employees/template.${format}`;
    // Use a hidden anchor so authentication cookies are sent with the request.
    const link = document.createElement("a");
    link.href = url;
    link.download = `synq_employees_template.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Bulk selection helpers ──────────────────────────────────────────────
  const clearSelection = () => setSelectedIds(new Set());

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = (visibleIds) => {
    setSelectedIds((prev) => {
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleInviteSelected = async () => {
    if (selectedIds.size === 0) return;
    setBulkAction("invite");
    try {
      const ids = Array.from(selectedIds);
      const { data } = await axios.post(
        `${API}/api/org/invite-selected`,
        { employee_ids: ids },
        { withCredentials: true }
      );
      const count = data.count || 0;
      const sent = data.emails_sent || 0;
      const skipped = (data.skipped || []).length;
      let msg = `Generated ${count} invite link${count === 1 ? "" : "s"}`;
      if (skipped > 0) msg += ` (${skipped} already accepted, skipped)`;
      if (sent === count && count > 0) msg += ` — all ${sent} email${sent === 1 ? "" : "s"} delivered.`;
      else if (sent > 0) msg += ` — ${sent} email${sent === 1 ? "" : "s"} delivered, copy the rest from the table.`;
      else if (count > 0) msg += " — email delivery is in test mode; copy the individual links from the table.";
      alert(msg);
      clearSelection();
      fetchEmployees();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || "Failed to invite selected employees");
    } finally {
      setBulkAction(null);
    }
  };

  const handleOpenNotify = () => {
    if (selectedIds.size === 0) return;
    setNotifyForm({ subject: "", message: "" });
    setNotifyError("");
    setShowNotifyModal(true);
  };

  const handleSendNotification = async () => {
    if (!notifyForm.subject.trim() || !notifyForm.message.trim()) {
      setNotifyError("Both subject and message are required.");
      return;
    }
    setBulkAction("notify");
    setNotifyError("");
    try {
      const ids = Array.from(selectedIds);
      const { data } = await axios.post(
        `${API}/api/org/notify-selected`,
        { employee_ids: ids, subject: notifyForm.subject, message: notifyForm.message },
        { withCredentials: true }
      );
      const count = data.count || 0;
      const sent = data.emails_sent || 0;
      const alerts = data.alerts_created || 0;
      alert(
        `Notification sent to ${count} employee${count === 1 ? "" : "s"}. ` +
        `${sent} email${sent === 1 ? "" : "s"} delivered, ${alerts} in-app alert${alerts === 1 ? "" : "s"} created.`
      );
      clearSelection();
      setShowNotifyModal(false);
    } catch (err) {
      console.error(err);
      setNotifyError(err.response?.data?.detail || "Failed to send notifications");
    } finally {
      setBulkAction(null);
    }
  };

  const handleClassify = async () => {
    try {
      await axios.post(`${API}/api/org/employees/classify`, {}, { withCredentials: true });
      fetchEmployees();
      fetchStats();
    } catch (err) {
      console.error(err);
    }
  };

  const filtered = employees.filter((e) => {
    const q = search.toLowerCase();
    return e.name.toLowerCase().includes(q) || e.email.toLowerCase().includes(q) || (e.team || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 animate-slide-up">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-5 h-5 text-slate-700" />
              <h1 className="font-outfit text-3xl font-black text-slate-900">Employee Management</h1>
            </div>
            <p className="font-ibm-plex text-sm text-slate-500">
              {employees.length} employee{employees.length !== 1 ? "s" : ""} in your organization
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx"
              onChange={handleCSVUpload}
              className="hidden"
              data-testid="csv-upload-input"
            />
            <div className="flex items-center rounded-lg border border-gray-300 overflow-hidden">
              <button
                data-testid="template-download-csv"
                onClick={() => handleDownloadTemplate("csv")}
                className="flex items-center gap-1.5 text-slate-700 font-outfit font-medium px-3 py-2 hover:bg-gray-50 transition-colors text-sm"
                title="Download CSV template with sample rows"
              >
                <Download className="w-4 h-4" />
                Template
              </button>
              <span className="w-px h-6 bg-gray-300" />
              <button
                data-testid="template-download-xlsx"
                onClick={() => handleDownloadTemplate("xlsx")}
                className="flex items-center gap-1.5 text-slate-700 font-outfit font-medium px-2.5 py-2 hover:bg-gray-50 transition-colors text-sm"
                title="Download Excel template with sample rows"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="text-xs">XLSX</span>
              </button>
            </div>
            <button
              data-testid="csv-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={csvLoading}
              className="flex items-center gap-2 border border-gray-300 text-slate-700 font-outfit font-medium px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              <Upload className="w-4 h-4" />
              {csvLoading ? "Uploading..." : "Import"}
            </button>
            <button
              data-testid="add-employee-btn"
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 bg-slate-900 text-white font-outfit font-medium px-4 py-2 rounded-lg hover:bg-slate-800 transition-colors text-sm"
            >
              <UserPlus className="w-4 h-4" />
              Add Employee
            </button>
          </div>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 animate-slide-up stagger-1">
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Total</p>
              <p className="font-outfit text-2xl font-black text-slate-900">{stats.total_employees}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Executives</p>
              <p className="font-outfit text-2xl font-black text-purple-600">{stats.roles?.executive || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Managers</p>
              <p className="font-outfit text-2xl font-black text-blue-600">{stats.roles?.manager || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">ICs</p>
              <p className="font-outfit text-2xl font-black text-slate-600">{stats.roles?.individual_contributor || 0}</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-400 mb-1">Accepted</p>
              <p className="font-outfit text-2xl font-black text-emerald-600">{stats.accepted || 0}</p>
            </div>
          </div>
        )}

        {/* CSV Result */}
        {csvResult && (
          <div className={`mb-4 p-4 rounded-lg border animate-slide-up ${csvResult.error ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
            {csvResult.error ? (
              <p className="font-ibm-plex text-sm text-red-700">{csvResult.error}</p>
            ) : (
              <div className="flex items-center justify-between">
                <p className="font-ibm-plex text-sm text-emerald-700">
                  <CheckCircle className="w-4 h-4 inline mr-1" />
                  Added {csvResult.added} employees, skipped {csvResult.skipped}
                  {csvResult.errors?.length > 0 && ` (${csvResult.errors.length} errors)`}
                </p>
                <button onClick={() => setCsvResult(null)} className="text-emerald-500 hover:text-emerald-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Search + Actions */}
        <div className="flex items-center gap-3 mb-4 animate-slide-up stagger-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              data-testid="employee-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or team..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
            />
          </div>
          <button
            data-testid="classify-btn"
            onClick={handleClassify}
            className="flex items-center gap-2 border border-gray-300 text-slate-700 font-outfit font-medium px-4 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <Shield className="w-4 h-4" />
            Auto-Classify
          </button>
          <button
            data-testid="invite-all-btn"
            onClick={handleInviteAll}
            className="flex items-center gap-2 border border-blue-200 text-blue-700 bg-blue-50 font-outfit font-medium px-4 py-2.5 rounded-lg hover:bg-blue-100 transition-colors text-sm"
          >
            <Send className="w-4 h-4" />
            Invite All
          </button>
        </div>

        {/* Employee Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-16 text-center animate-fade-in">
            <Users className="w-12 h-12 text-slate-200 mx-auto mb-4" />
            <h3 className="font-outfit text-xl font-bold text-slate-900 mb-2">
              {search ? "No employees match your search" : "No employees yet"}
            </h3>
            <p className="font-ibm-plex text-slate-500 text-sm mb-6">
              {search ? "Try a different search term" : "Add employees manually or import a CSV file"}
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden animate-slide-up stagger-3">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left pl-4 pr-2 py-3 w-10">
                    <button
                      type="button"
                      data-testid="select-all-checkbox"
                      onClick={() => toggleAllVisible(filtered.map((e) => e.id))}
                      className="p-1 rounded hover:bg-gray-200 transition-colors"
                      title={selectedIds.size > 0 && filtered.every((e) => selectedIds.has(e.id))
                        ? "Deselect all visible"
                        : "Select all visible"}
                    >
                      {filtered.length > 0 && filtered.every((e) => selectedIds.has(e.id)) ? (
                        <CheckSquare className="w-4 h-4 text-slate-900" />
                      ) : (
                        <Square className="w-4 h-4 text-slate-400" />
                      )}
                    </button>
                  </th>
                  {["Name", "Email", "Team", "Role", "Classification", "Status", ""].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-ibm-plex text-xs font-semibold text-slate-500 uppercase tracking-widest">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp) => {
                  const rc = ROLE_COLORS[emp.classified_role] || ROLE_COLORS.individual_contributor;
                  const RoleIcon = ROLE_ICONS[emp.classified_role] || User;
                  const isSelected = selectedIds.has(emp.id);
                  return (
                    <tr key={emp.id} data-testid={`employee-row-${emp.id}`}
                      className={`border-b border-gray-100 transition-colors ${isSelected ? "bg-blue-50/50" : "hover:bg-gray-50"}`}>
                      <td className="pl-4 pr-2 py-3">
                        <button
                          type="button"
                          data-testid={`row-checkbox-${emp.id}`}
                          onClick={() => toggleOne(emp.id)}
                          className="p-1 rounded hover:bg-gray-100 transition-colors"
                          title={isSelected ? "Deselect" : "Select"}
                        >
                          {isSelected ? (
                            <CheckSquare className="w-4 h-4 text-blue-600" />
                          ) : (
                            <Square className="w-4 h-4 text-slate-300" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-outfit font-semibold text-slate-900 text-sm">{emp.name}</span>
                      </td>
                      <td className="px-4 py-3 font-ibm-plex text-sm text-slate-500">{emp.email}</td>
                      <td className="px-4 py-3 font-ibm-plex text-sm text-slate-500">{emp.team || "—"}</td>
                      <td className="px-4 py-3 font-ibm-plex text-sm text-slate-500">{emp.role_manual || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-ibm-plex font-medium ${rc.bg} ${rc.text}`}>
                          <RoleIcon className="w-3 h-3" />
                          {ROLE_LABELS[emp.classified_role] || "IC"}
                          {emp.direct_reports_count > 0 && (
                            <span className="text-[10px] opacity-70">({emp.direct_reports_count})</span>
                          )}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <InviteStatusBadge status={emp.invite_status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`invite-btn-${emp.id}`}
                            onClick={() => handleInvite(emp.id)}
                            disabled={inviteLoading[emp.id]}
                            className="p-1.5 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                            title="Generate invite link"
                          >
                            {copiedToken === emp.id ? (
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <Link2 className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            data-testid={`delete-emp-${emp.id}`}
                            onClick={() => handleDelete(emp.id)}
                            className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── Sticky Bulk Action Bar ────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-800 flex items-center gap-3 px-4 py-3 animate-slide-up"
        >
          <div className="flex items-center gap-2 pr-3 border-r border-slate-700">
            <CheckSquare className="w-4 h-4 text-blue-300" />
            <span data-testid="bulk-selection-count" className="font-outfit font-bold text-sm">
              {selectedIds.size} selected
            </span>
          </div>
          <button
            data-testid="bulk-invite-btn"
            onClick={handleInviteSelected}
            disabled={bulkAction === "invite"}
            className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-400 disabled:opacity-60 font-outfit font-semibold text-xs px-3 py-2 rounded-lg transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            {bulkAction === "invite" ? "Inviting…" : `Invite Selected (${selectedIds.size})`}
          </button>
          <button
            data-testid="bulk-notify-btn"
            onClick={handleOpenNotify}
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 font-outfit font-semibold text-xs px-3 py-2 rounded-lg transition-colors"
          >
            <Bell className="w-3.5 h-3.5" />
            Send Notification
          </button>
          <button
            data-testid="bulk-clear-btn"
            onClick={clearSelection}
            className="flex items-center gap-1.5 text-slate-300 hover:text-white font-outfit text-xs px-2 py-2 rounded-lg transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Clear
          </button>
        </div>
      )}

      {/* ─── Notification Modal ────────────────────────────────────────────── */}
      {showNotifyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => bulkAction !== "notify" && setShowNotifyModal(false)}
          data-testid="notify-modal"
        >
          <div
            className="bg-white rounded-xl w-full max-w-lg mx-4 p-6 shadow-xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-amber-500" />
                <h3 className="font-outfit text-lg font-bold text-slate-900">
                  Send Notification
                </h3>
              </div>
              <button
                onClick={() => setShowNotifyModal(false)}
                disabled={bulkAction === "notify"}
                className="text-slate-400 hover:text-slate-600 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="font-ibm-plex text-xs text-slate-500 mb-4">
              This will email {selectedIds.size} employee{selectedIds.size === 1 ? "" : "s"} and
              create an in-app alert for anyone who has already accepted their invite.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  Subject *
                </label>
                <input
                  data-testid="notify-subject-input"
                  type="text"
                  value={notifyForm.subject}
                  onChange={(e) => setNotifyForm((f) => ({ ...f, subject: e.target.value }))}
                  placeholder="e.g. Sprint pulse this week — please respond by Friday"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  Message *
                </label>
                <textarea
                  data-testid="notify-message-input"
                  rows={5}
                  value={notifyForm.message}
                  onChange={(e) => setNotifyForm((f) => ({ ...f, message: e.target.value }))}
                  placeholder="Write a short message. SYNQ will format this for email and in-app alerts."
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent resize-none"
                />
              </div>
              {notifyError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700 font-ibm-plex flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {notifyError}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowNotifyModal(false)}
                  disabled={bulkAction === "notify"}
                  className="flex-1 border border-gray-300 text-slate-700 font-outfit font-semibold py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-sm disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  data-testid="notify-send-btn"
                  onClick={handleSendNotification}
                  disabled={bulkAction === "notify"}
                  className="flex-1 bg-amber-500 text-white font-outfit font-semibold py-2.5 rounded-lg hover:bg-amber-400 transition-colors text-sm disabled:opacity-60"
                >
                  {bulkAction === "notify" ? "Sending…" : `Send to ${selectedIds.size}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-xl w-full max-w-md mx-4 p-6 shadow-xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-outfit text-lg font-bold text-slate-900">Add Employee</h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleAddEmployee} className="space-y-4">
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  Name *
                </label>
                <input
                  data-testid="emp-name-input"
                  type="text" required value={addForm.name}
                  onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                  placeholder="Full name"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  Email *
                </label>
                <input
                  data-testid="emp-email-input"
                  type="email" required value={addForm.email}
                  onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                  placeholder="employee@company.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                    Team
                  </label>
                  <input
                    data-testid="emp-team-input"
                    type="text" value={addForm.team}
                    onChange={(e) => setAddForm({ ...addForm, team: e.target.value })}
                    placeholder="e.g. Engineering"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                    Project
                  </label>
                  <input
                    data-testid="emp-project-input"
                    type="text" value={addForm.project}
                    onChange={(e) => setAddForm({ ...addForm, project: e.target.value })}
                    placeholder="e.g. SYNQ v2"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-ibm-plex font-semibold text-slate-600 uppercase tracking-widest mb-1.5">
                  Reporting Manager Email
                </label>
                <input
                  data-testid="emp-manager-input"
                  type="email" value={addForm.reporting_manager_email}
                  onChange={(e) => setAddForm({ ...addForm, reporting_manager_email: e.target.value })}
                  placeholder="manager@company.com"
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                />
              </div>
              {addError && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700 font-ibm-plex flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {addError}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="flex-1 border border-gray-300 text-slate-700 font-outfit font-semibold py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-sm">
                  Cancel
                </button>
                <button
                  data-testid="emp-submit-btn"
                  type="submit" disabled={addLoading}
                  className="flex-1 bg-slate-900 text-white font-outfit font-semibold py-2.5 rounded-lg hover:bg-slate-800 transition-colors text-sm disabled:opacity-60">
                  {addLoading ? "Adding..." : "Add Employee"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
